import { useEffect, useMemo, useRef, useState } from "react";
import { toCanvas } from "html-to-image";
import QRCode from "qrcode";
import {
  CalendarBlankIcon,
  CheckCircleIcon,
  CopyIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  HouseIcon,
  KeyIcon,
  MapPinIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  RulerIcon,
  ShieldCheckIcon,
  ShieldWarningIcon,
  TShirtIcon,
  UserIcon,
  WarningCircleIcon,
  WhatsappLogoIcon,
} from "@phosphor-icons/react";
import isotipo from "../../TALLER/isotipo.webp";

const etiquetas = {
  PERRO: "Perro", GATO: "Gato", OTRO: "Otra mascota",
  MACHO: "Macho", HEMBRA: "Hembra",
  MUY_PEQUENO: "Muy pequeño", PEQUENO: "Pequeño", MEDIANO: "Mediano",
  GRANDE: "Grande", MUY_GRANDE: "Muy grande",
  NEGRO: "Negro", BLANCO: "Blanco", MARRON: "Marrón", CAFE_CLARO: "Café claro",
  BEIGE: "Beige", CREMA: "Crema", DORADO: "Dorado", AMARILLO: "Amarillo",
  ROJIZO: "Rojizo", GRIS: "Gris", CANELA: "Canela", ATIGRADO: "Atigrado",
  MANCHADO: "Manchado",
  HOMBRE: "Masculino", MUJER: "Femenino", NO_ESPECIFICA: "No especificado",
  DELGADA: "Delgada", MEDIANA: "Mediana", ROBUSTA: "Robusta",
  CASTANO_OSCURO: "Castaño oscuro", CASTANO: "Castaño",
  CASTANO_CLARO: "Castaño claro", RUBIO: "Rubio", RUBIO_OSCURO: "Rubio oscuro",
  PELIRROJO: "Pelirrojo", CANOSO: "Canoso", TENIDO: "Teñido", CALVO: "Calvo",
  MARRON_OSCURO: "Marrón oscuro", MIEL: "Miel", VERDE: "Verde", AZUL: "Azul",
  HETEROCROMIA: "Dos colores",
};

const titulos = {
  PERRO: "¡ENCONTRÉ ESTE PERRITO!",
  GATO: "¡ENCONTRÉ ESTE GATICO!",
  OTRO: "¡ENCONTRÉ ESTA BELLEZA!",
};

const MENSAJE_DUENO = "hola, creo que soy el dueño, podrias darme mas informacion";

function fechaLarga(fecha) {
  return new Intl.DateTimeFormat("es-CO", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  }).format(new Date(`${fecha}T00:00:00Z`));
}

function sanitizarNombreParaAfiche(nombre) {
  return nombre?.trim().toLocaleUpperCase("es-CO") || "TU MASCOTA";
}

function nombreArchivoHistoria(variante, datos) {
  const nombre = variante === "persona" ? datos.nombrePersona : datos.nombreMascota;
  const identificador = nombre || (variante === "encontrada" ? "mascota-encontrada" : "reporte");
  const seguro = identificador
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `encuentrayayuda-${seguro || "historia"}.jpg`;
}

function obtenerContenidoFlyer(variante, datos) {
  if (variante === "persona") {
    return {
      descripcion: "persona desaparecida",
      fecha: datos.fechaEvento,
      hora: datos.horaEvento,
      mensaje: `Hola, tengo información sobre ${datos.nombrePersona}. ¿Cómo puedo ayudar?`,
      titulo: `¡BUSCAMOS A ${sanitizarNombreParaAfiche(datos.nombrePersona)}!`,
    };
  }
  if (variante === "perdida") {
    return {
      descripcion: "mascota perdida",
      fecha: datos.fechaPerdida,
      hora: datos.horaPerdida,
      mensaje: `Hola, creo que vi a ${datos.nombreMascota}. ¿Podrías darme más información?`,
      titulo: `¡BUSCAMOS A ${sanitizarNombreParaAfiche(datos.nombreMascota)}!`,
    };
  }
  return {
    descripcion: "mascota encontrada",
    fecha: datos.fechaHallazgo,
    hora: datos.horaHallazgo,
    mensaje: MENSAJE_DUENO,
    titulo: titulos[datos.tipoMascota] ?? titulos.OTRO,
  };
}

function leerComoDataUrl(archivo) {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(lector.result);
    lector.onerror = () => rechazar(lector.error);
    lector.readAsDataURL(archivo);
  });
}

async function esperarImagenes(contenedor) {
  const imagenes = [...contenedor.querySelectorAll("img")];
  await Promise.all(imagenes.map(async (imagen) => {
    if (imagen.complete && imagen.naturalWidth > 0) return;
    await imagen.decode();
  }));
}

function rectanguloEnCanvas(elemento, posterRect, escala) {
  const rectangulo = elemento.getBoundingClientRect();
  return {
    x: (rectangulo.left - posterRect.left) * escala,
    y: (rectangulo.top - posterRect.top) * escala,
    ancho: rectangulo.width * escala,
    alto: rectangulo.height * escala,
  };
}

function escalarRectangulo(rectangulo, escala) {
  return Object.fromEntries(
    Object.entries(rectangulo).map(([clave, valor]) => [clave, valor * escala]),
  );
}

function claseNombreFlyer(nombre) {
  if (nombre.length > 34) return "is-very-long";
  if (nombre.length > 24) return "is-long";
  return "";
}

function dibujarImagenAjustada(contexto, imagen, destino, modo = "cover", radio = 0) {
  const proporcionOrigen = imagen.naturalWidth / imagen.naturalHeight;
  const proporcionDestino = destino.ancho / destino.alto;
  let sx = 0;
  let sy = 0;
  let sw = imagen.naturalWidth;
  let sh = imagen.naturalHeight;
  let dx = destino.x;
  let dy = destino.y;
  let dw = destino.ancho;
  let dh = destino.alto;

  if (modo === "cover") {
    if (proporcionOrigen > proporcionDestino) {
      sw = imagen.naturalHeight * proporcionDestino;
      sx = (imagen.naturalWidth - sw) / 2;
    } else {
      sh = imagen.naturalWidth / proporcionDestino;
      sy = (imagen.naturalHeight - sh) / 2;
    }
  } else if (proporcionOrigen > proporcionDestino) {
    dh = destino.ancho / proporcionOrigen;
    dy += (destino.alto - dh) / 2;
  } else {
    dw = destino.alto * proporcionOrigen;
    dx += (destino.ancho - dw) / 2;
  }

  contexto.save();
  if (radio > 0) {
    contexto.beginPath();
    contexto.roundRect(dx, dy, dw, dh, radio);
    contexto.clip();
  }
  contexto.drawImage(imagen, sx, sy, sw, sh, dx, dy, dw, dh);
  contexto.restore();
}

function ReportePublicado({ resultado, datos, fotos, contacto, onInicio, variante = "encontrada", soloFlyer = false, textoBotonInicio = "Volver al inicio" }) {
  const [copiado, setCopiado] = useState(false);
  const [qr, setQr] = useState("");
  const [fotoPoster, setFotoPoster] = useState("");
  const [jpgPoster, setJpgPoster] = useState("");
  const [errorImagen, setErrorImagen] = useState("");
  const posterRef = useRef(null);
  const telefonoRef = useRef(null);
  const logoRef = useRef(null);
  const fotoRef = useRef(null);
  const qrRef = useRef(null);
  const telefono = `${contacto.codigoPais}${contacto.telefono}`;
  const telefonoVisible = contacto.codigoPais ? `+${contacto.codigoPais} ${contacto.telefono}` : `+${contacto.telefono}`;
  const fotoParaFlyer = useMemo(
    () => fotos.find((foto) => foto.alto > foto.ancho) ?? fotos[0],
    [fotos],
  );
  const esPerdida = variante === "perdida";
  const esPersona = variante === "persona";
  const nombrePersona = sanitizarNombreParaAfiche(datos.nombrePersona);
  const contenidoFlyer = obtenerContenidoFlyer(variante, datos);
  const enlaceWhatsapp = useMemo(
    () => `https://wa.me/${telefono}?text=${encodeURIComponent(contenidoFlyer.mensaje)}`,
    [contenidoFlyer.mensaje, telefono],
  );
  const { descripcion, fecha: fechaPoster, hora: horaPoster, titulo: tituloPoster } = contenidoFlyer;

  useEffect(() => {
    QRCode.toDataURL(enlaceWhatsapp, {
      errorCorrectionLevel: "M", margin: 1, width: 260,
      color: { dark: "#123b5d", light: "#ffffff" },
    }).then(setQr).catch(() => setErrorImagen("No se pudo generar el código QR."));
  }, [enlaceWhatsapp]);

  useEffect(() => {
    const archivo = fotoParaFlyer?.archivo;
    const urlRemota = fotoParaFlyer?.url;
    if (!archivo && !urlRemota) return;
    const prepararFoto = archivo
      ? leerComoDataUrl(archivo)
      : fetch(urlRemota).then((respuesta) => {
        if (!respuesta.ok) throw new Error("No se pudo descargar la foto");
        return respuesta.blob();
      }).then(leerComoDataUrl);
    prepararFoto
      .then(setFotoPoster)
      .catch(() => setErrorImagen("No pudimos preparar la foto para el afiche."));
  }, [fotoParaFlyer]);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(resultado.codigoGestion);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch { setCopiado(false); }
  }

  useEffect(() => {
    if (!posterRef.current || !telefonoRef.current || !qr || !fotoPoster) return;
    let vigente = true;

    async function generarJpg() {
      setErrorImagen("");
      try {
        await document.fonts?.ready;
        await esperarImagenes(posterRef.current);
        const posterRect = posterRef.current.getBoundingClientRect();
        const telefonoRect = telefonoRef.current.getBoundingClientRect();
        const logoDestino = rectanguloEnCanvas(logoRef.current, posterRect, 1);
        const fotoDestino = rectanguloEnCanvas(fotoRef.current, posterRect, 1);
        const qrDestino = rectanguloEnCanvas(qrRef.current, posterRect, 1);
        const canvas = await toCanvas(posterRef.current, {
          cacheBust: false,
          width: 540,
          height: 960,
          pixelRatio: 2,
          backgroundColor: "#f7fbff",
        });
        const escala = canvas.width / posterRect.width;
        const contexto = canvas.getContext("2d");

        // Safari/iOS puede omitir las imagenes al clonar el DOM. Se pintan de
        // nuevo desde los elementos originales ya decodificados, conservando
        // exactamente su posicion dentro del afiche.
        dibujarImagenAjustada(
          contexto,
          logoRef.current,
          escalarRectangulo(logoDestino, escala),
          "contain",
        );
        dibujarImagenAjustada(
          contexto,
          fotoRef.current,
          escalarRectangulo(fotoDestino, escala),
          "cover",
          (esPerdida ? 120 : esPersona ? 18 : 16) * escala,
        );
        dibujarImagenAjustada(
          contexto,
          qrRef.current,
          escalarRectangulo(qrDestino, escala),
          "contain",
        );

        if (!esPersona) {
          contexto.fillStyle = "#ffffff";
          contexto.font = `800 ${14 * escala}px Nunito, sans-serif`;
          contexto.textAlign = "center";
          contexto.textBaseline = "middle";
          contexto.fillText(
            `WhatsApp: ${telefonoVisible}`,
            (telefonoRect.left - posterRect.left + telefonoRect.width / 2) * escala,
            (telefonoRect.top - posterRect.top + telefonoRect.height / 2) * escala,
          );
        }
        if (vigente) setJpgPoster(canvas.toDataURL("image/jpeg", 0.92));
      } catch (error) {
        console.error("Error al generar el afiche:", error);
        if (vigente) setErrorImagen("No pudimos generar la imagen. Intenta recargar la página.");
      }
    }

    generarJpg();
    return () => { vigente = false; };
  }, [esPerdida, esPersona, fotoPoster, qr, telefonoVisible]);

  return (
    <section className="published" aria-labelledby="published-title">
      {!soloFlyer && <><span className="published__icon"><CheckCircleIcon weight="fill" /></span><h2 id="published-title">Tu reporte fue publicado</h2><p>Guarda y comparte esta imagen para que llegue a más personas.</p></>}

      <article className={`share-poster share-poster--${variante}${jpgPoster ? " share-poster--source" : ""}`} ref={posterRef} aria-hidden={jpgPoster ? "true" : undefined}>
        <header className="share-poster__brand">
          <img src={isotipo} alt="" ref={logoRef} />
          <div><strong>encuentra<span>y</span>ayuda.com</strong><small>Personas y mascotas, juntos de nuevo.</small></div>
        </header>
        {esPersona ? <>
          <h3><span>¡BUSCAMOS A!</span><strong className={claseNombreFlyer(nombrePersona)}>{nombrePersona}</strong></h3>
          <p className="share-poster__person-label"><UserIcon weight="fill" /> PERSONA DESAPARECIDA</p>
          <div className="share-poster__person-main">
            <div className="share-poster__person-photo-column">
              {fotoPoster && <div className="share-poster__person-photo-frame"><img className="share-poster__pet" src={fotoPoster} alt={`Fotografía de ${datos.nombrePersona}`} ref={fotoRef} /></div>}
            </div>
            <div className="share-poster__person-data">
              <p><CalendarBlankIcon weight="duotone" /><span><strong>Edad:</strong> {datos.edadAproximada || "No especificada"}{datos.edadAproximada && " años"}</span></p>
              <p><RulerIcon weight="duotone" /><span><strong>Estatura:</strong> {datos.estaturaAproximada || "No especificada"}{datos.estaturaAproximada && " m"}</span></p>
              <p><UserIcon weight="duotone" /><span><strong>Contextura:</strong> {etiquetas[datos.contextura] || "No especificada"}</span></p>
              <p><UserIcon weight="duotone" /><span><strong>Cabello:</strong> {(datos.coloresCabello || []).map((color) => etiquetas[color]).join(", ") || etiquetas[datos.colorCabello] || "No especificado"}</span></p>
              <p><EyeIcon weight="duotone" /><span><strong>Ojos:</strong> {etiquetas[datos.colorOjos] || "No especificados"}</span></p>
              <p><TShirtIcon weight="duotone" /><span><strong>Ropa al desaparecer:</strong><br />{datos.vestimenta || "No especificada"}</span></p>
            </div>
          </div>
          <div className="share-poster__last-seen"><MapPinIcon weight="fill" /><div><strong>VISTO POR ÚLTIMA VEZ</strong><p>{fechaLarga(fechaPoster)} · {horaPoster}</p><p>{datos.direccionEvento}</p></div></div>
          <div className="share-poster__person-help"><span><ShieldCheckIcon weight="duotone" /></span><div><strong>AYÚDANOS A ENCONTRARLO</strong><p>Si tienes información, por favor comunícate con nosotros.</p></div></div>
          <div className="share-poster__person-contact">
            <div className="share-poster__contact-details"><strong>CONTACTO</strong><div className="share-poster__contact-items">
              <div><WhatsappLogoIcon weight="fill" /><span><b>WhatsApp</b><small>{telefonoVisible}</small></span></div>
              <div><PhoneIcon weight="fill" /><span><b>Llamada</b><small>{telefonoVisible}</small></span></div>
              <div><EnvelopeSimpleIcon weight="fill" /><span><b>Correo</b><small>{contacto.email || "Correo protegido"}</small></span></div>
            </div></div>
            <div className="share-poster__qr"><strong>MÁS INFORMACIÓN</strong>{qr && <img src={qr} alt="Código QR para contactar por WhatsApp" ref={qrRef} />}<small>Escanea para escribir</small></div>
          </div>
          <div className="share-poster__person-privacy"><ShieldCheckIcon weight="duotone" /><p><strong>TU INFORMACIÓN ES CONFIDENCIAL</strong><br />Comparte esta historia para ayudar a que regrese a casa.</p><PaperPlaneTiltIcon weight="fill" /></div>
          <span className="share-poster__phone-canvas" ref={telefonoRef}>&nbsp;</span>
        </> : <>
          {esPerdida && <p className="share-poster__search-label">PELUDITO DESAPARECIDO</p>}
          <h3>{tituloPoster}</h3>
          <p className="share-poster__date"><MapPinIcon weight="fill" /> {esPerdida ? "Se perdió el" : "Encontrado el"} {fechaLarga(fechaPoster)}</p>
          <div className="share-poster__main">
            {fotoPoster && (esPerdida ? <div className="share-poster__pet-frame"><img className="share-poster__pet" src={fotoPoster} alt="Mascota perdida" ref={fotoRef} /></div> : <img className="share-poster__pet" src={fotoPoster} alt="Mascota encontrada" ref={fotoRef} />)}
            <dl><div><dt>Tipo</dt><dd>{etiquetas[datos.tipoMascota]}</dd></div><div><dt>Color</dt><dd>{etiquetas[datos.colorPrincipalPelaje]}</dd></div><div><dt>Tamaño</dt><dd>{etiquetas[datos.tamano]}</dd></div><div><dt>Sexo</dt><dd>{etiquetas[datos.sexo]}</dd></div></dl>
          </div>
          <div className="share-poster__details"><div><strong>{esPerdida ? "¿Dónde se perdió?" : "¿Dónde lo encontré?"}</strong><p>{datos.direccionHallazgo}</p>{datos.puntoReferencia && <p>{datos.puntoReferencia}</p>}<small>{horaPoster}</small></div><div className="share-poster__qr"><strong>CONTACTAR POR WHATSAPP</strong>{qr && <img src={qr} alt="Código QR para contactar por WhatsApp" ref={qrRef} />}<small>Escanea para escribir</small></div></div>
          <footer><WhatsappLogoIcon weight="fill" /><div><strong>{esPerdida ? `¿Has visto a ${datos.nombreMascota}?` : "¿Conoces a su familia?"}</strong><span ref={telefonoRef} aria-label="Número de WhatsApp">&nbsp;</span></div></footer>
        </>}
      </article>

      {jpgPoster && <>
        <img className="published__generated-poster" src={jpgPoster} alt={`Historia de ${descripcion} lista para compartir`} />
        <a className="published__download" href={jpgPoster} download={nombreArchivoHistoria(variante, datos)}>
          <DownloadSimpleIcon weight="bold" /> Descargar imagen
        </a>
      </>}
      {!jpgPoster && !errorImagen && <p className="published__generating" role="status">Generando imagen JPG…</p>}
      {errorImagen && <p className="published__image-error" role="alert">{errorImagen}</p>}

      {!soloFlyer && !esPerdida && !esPersona && <aside className="owner-warning">
        <ShieldWarningIcon weight="duotone" />
        <div><strong>Verifica antes de entregar la mascota</strong><p>Pide fotos anteriores y detalles que solo su familia conocería. Haz también una pregunta de control falsa, por ejemplo: “¿Tiene una mancha en forma de estrella?”. Si la persona confirma una característica que no existe, es una señal de alerta: no entregues la mascota y solicita más pruebas.</p></div>
      </aside>}

      {!soloFlyer && <><div className="published__code">
        <div className="published__code-heading"><KeyIcon weight="duotone" /><strong>Tu código de gestión</strong></div>
        <output aria-label="Código de gestión">{resultado.codigoGestion}</output>
        <button type="button" onClick={copiar}><CopyIcon weight="bold" /> {copiado ? "Copiado" : "Copiar código"}</button>
        <p className="published__code-warning"><WarningCircleIcon weight="fill" />Guárdalo ahora. Es la única forma de editar o cerrar el reporte y no se puede recuperar.</p>
      </div>

      <p className="published__photos-ok">{resultado.multimedia.length} foto{resultado.multimedia.length === 1 ? "" : "s"} subida{resultado.multimedia.length === 1 ? "" : "s"} correctamente.</p>
      </>}
      <button className="published__home" type="button" onClick={onInicio}><HouseIcon weight="fill" /> {textoBotonInicio}</button>
    </section>
  );
}

export default ReportePublicado;
